import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppointmentTypeCards, QuizResultContainer } from './Question.styled';
import { usePatient } from 'hooks/usePatient/usePatient';
import { AppointmentType } from 'kb-shared';
import { isOnlineBookingUnavailableForAppointmentType } from 'kb-shared/booking/utils/onlineBookingAvailability';
import { appointmentSupportedInPartnerClinics } from 'kb-shared/utilities/appointment.helper';
import { AdditionalInfoPanel } from 'screens/Book/components/AdditionalInfoPanel';
import HelpText from 'screens/Book/components/HelpText';
import { OnlineBookingUnavailableMessage } from 'screens/Book/components/OnlineBookingUnavailableMessage';
import { analytics } from 'utilities/analytics';
import { pageUrl } from 'utilities/pageUrl';

import { BookingQuizCategory } from '../BookingQuiz.types';
import { AppointmentTypeCard } from './AppointmentTypeCard';
import { PartnerClinicCard } from './PartnerClinicCard';

interface QuizResultProps {
  appointmentTypes: Array<AppointmentType>;
  category?: BookingQuizCategory;
  isLoggedIn: boolean | undefined;
}

export const QuizResult = ({
  appointmentTypes,
  category,
  isLoggedIn
}: QuizResultProps): JSX.Element => {
  const navigate = useNavigate();
  const { patient } = usePatient();

  const showPartnerClinicList =
    patient?.patientMembership &&
    patient?.patientMembership?.membershipId &&
    patient?.patientMembership?.membership?.partnerClinicsEnabled &&
    category === 'fertility' &&
    appointmentTypes.length > 0 &&
    appointmentSupportedInPartnerClinics(appointmentTypes[0].id);
  const hasBookableAppointmentTypes = appointmentTypes.some(
    appointmentType => !isOnlineBookingUnavailableForAppointmentType({ appointmentType })
  );

  useEffect(() => {
    appointmentTypes.forEach(appointmentType => {
      analytics.page(analytics.PAGES.APPOINTMENT_RECOMMENDATION, {
        appointment_category: category,
        appointment_type_name: appointmentType.name,
        appointment_type_ids: appointmentTypes.map(apptType => apptType.id)
      });
    });
  }, [appointmentTypes, category]);

  const handleClick = () => {
    const appointmentId = [...appointmentTypes].pop()?.id;
    navigate(
      pageUrl.booking.index({
        step: 'location',
        appointmentId,
        partnerClinicSearch: 'search'
      }),
      {
        state: {
          data: { partnerClinicSearch: 'search', selectedStep: 'location' }
        }
      }
    );
  };

  const handleAppointmentClick = (appointmentType: AppointmentType): void => {
    const scheduleUrl = isLoggedIn
      ? pageUrl.booking.index({ step: 'location', appointmentId: appointmentType.id })
      : pageUrl.createAccount({ step: 'location', appointmentId: appointmentType.id });
    analytics.track(analytics.EVENTS.APPOINTMENT_SELECTED);
    navigate(scheduleUrl);
  };

  return (
    <QuizResultContainer>
      <AppointmentTypeCards>
        {appointmentTypes.map(appointmentType => {
          const onlineBookingUnavailable = isOnlineBookingUnavailableForAppointmentType({
            appointmentType
          });

          return (
            <AppointmentTypeCard
              key={appointmentType.id}
              appointmentType={appointmentType}
              footerContent={
                onlineBookingUnavailable ? <OnlineBookingUnavailableMessage compact /> : undefined
              }
              onClick={
                onlineBookingUnavailable ? undefined : () => handleAppointmentClick(appointmentType)
              }
            />
          );
        })}
        {isLoggedIn && showPartnerClinicList && hasBookableAppointmentTypes && (
          <PartnerClinicCard onClick={handleClick} />
        )}
      </AppointmentTypeCards>

      {category === 'wellness' && (
        <AdditionalInfoPanel>
          In order to provide you with the earliest availability for your appointment, the
          appointment times listed include all providers for this location. If you have a specific
          provider preference, please call us to book: <a href="tel://855-563-263">855-563-263</a>
        </AdditionalInfoPanel>
      )}
      {!isLoggedIn && (
        <HelpText
          text="Looking for another service?"
          linkText="View all Services &amp; Pricing"
          linkUrl="https://kindbody.com/services-pricing/"
        />
      )}
    </QuizResultContainer>
  );
};
